/**
 * ProductEditPage — Full page edit with drag & drop image reorder + variants.
 * Supports: New pricing model + Stock Intelligence + legacy price backward compat
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ArrowLeft, Upload, X, Star, Check, Loader2,
  GripVertical, AlertCircle, Trash2, Plus, Tag, Info, ChevronDown, ChevronUp, Image, Palette
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  fetchProduct, updateProduct,
  uploadProductImage, uploadVariantImage, deleteImage, setCoverImage, reorderImages,
  fetchCategories, fetchUnits,
  fetchVariants, createVariant, updateVariant, deleteVariant,
} from '../../api/supplierApi';
import SecureImage from '../../components/supplier/SecureImage';
import { apiClient } from '../../lib/apiClient';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'TRY'];
const SEL = "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";

const LOGISTIC_COMPANY_CATEGORY_ID = 'cfd92a00-4082-4378-80fa-35c70b1259f7';
const FORKLIFT_PROVIDER_SUBCATEGORY_ID = 'd9ca85a6-d82d-410b-aadb-79e423ed897e';
const FORKLIFT_PRESETS = [
  { key: 'standard_1_5t', label: 'Standart Forklift 1.5 Ton' },
  { key: 'standard_2t',   label: 'Standart Forklift 2 Ton'   },
  { key: 'standard_3t',   label: 'Standart Forklift 3 Ton'   },
  { key: 'standard_5t',   label: 'Standart Forklift 5 Ton'   },
  { key: 'standard_7t',   label: 'Standart Forklift 7 Ton'   },
];

// PRODUCT_TEMPLATES sabit listesi kaldırıldı — şablonlar artık API'den dinamik olarak yükleniyor.

const STOCK_POLICIES = [
  { value: 'untracked', label: 'Takip Edilmiyor', desc: 'Stok sayısı izlenmez' },
  { value: 'strict', label: 'Katı Stok', desc: 'Stok bitince sipariş alınmaz' },
  { value: 'flexible_supply', label: 'Esnek Tedarik', desc: 'Stok bitince "temin edilebilir" mesajı gösterilir' },
];

const PRICING_LABELS = {
  daily: 'Günlük',
  weekly: 'Haftalık',
  monthly: 'Aylık',
  one_time: 'Tek Seferlik',
  event_duration: 'Etkinlik Süresi',
};

// ── Section Header ─────────────────────────────────────────────────────────
function SH({ step, title, desc }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
      <div className="h-7 w-7 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{step}</div>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

// ── Pricing Input Row ──────────────────────────────────────────────────────
function PricingRow({ label, pricingKey, value, currency, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <Label className="text-xs font-semibold text-gray-500 uppercase w-36 shrink-0">{label}</Label>
      <div className="relative flex-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">{currency}</span>
        <Input type="number" value={value} onChange={e => onChange(pricingKey, e.target.value)}
          className="rounded-xl pl-12" placeholder="0.00" min="0" step="0.01"
          data-testid={`pricing-edit-${pricingKey}`} />
      </div>
    </div>
  );
}

// ── Draggable Image ────────────────────────────────────────────────────────
function DraggableImage({ img, index, onCover, onDelete, dragging, onDragStart, onDragEnter, onDragEnd }) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      className={`relative group h-24 w-24 shrink-0 rounded-xl overflow-hidden border-2 cursor-grab transition-all ${
        dragging ? 'opacity-50 scale-95 border-amber-400' : 'border-gray-100 hover:border-gray-300'
      }`}
    >
      {/* Drag handle */}
      <div className="absolute top-1 left-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 rounded p-0.5">
        <GripVertical className="h-3 w-3 text-white" />
      </div>
      <SecureImage src={img.thumb_url || img.url} alt="" className="w-full h-full object-cover" />
      {img.is_cover && (
        <span className="absolute top-1 right-1 bg-amber-500 text-white rounded text-[9px] px-1 font-bold flex items-center gap-0.5 z-10">
          <Star className="h-2.5 w-2.5" />
        </span>
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
        {!img.is_cover && (
          <button onClick={onCover} title="Kapak yap" className="text-white hover:text-amber-300 p-1">
            <Star className="h-3.5 w-3.5" />
          </button>
        )}
        <button onClick={onDelete} className="text-white hover:text-red-400 p-1">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function ProductEditPage({ productId, tr, onBack, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [productProfiles, setProductProfiles] = useState([]);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [variantImages, setVariantImages] = useState({}); // { variant_id: [...imageObjs] }
  const [uploadingVariant, setUploadingVariant] = useState(null); // variant_id | null
  const [expandedVariant, setExpandedVariant] = useState(null); // variant_id | null
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dragIdx, setDragIdx] = useState(null);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const dropRef = useRef(null);
  const [allPatternGroups, setAllPatternGroups] = useState([]);
  const [availablePatternItems, setAvailablePatternItems] = useState([]);
  const [selectedPatternIds, setSelectedPatternIds] = useState(new Set());
  const [openColorPicker, setOpenColorPicker] = useState(null); // variant index with open picker

  const [form, setForm] = useState({
    title: '', title_tr: '', title_en: '', description: '', description_tr: '', description_en: '',
    main_category_id: '', subcategory_id: '', category: '',
    currency: 'USD',
    product_code: '',
    min_order_quantity: '', max_order_quantity: '', unit_id: '', unit: '',
    lead_time_days: '', is_active: true, requires_supplier_confirmation: false,
    min_rental_period_days: '',
    // Product type
    product_type: 'rental',
    service_pricing_type: 'fixed',
    items_per_carton: '',
    // Yeni Pricing Model
    pricing_mode_supported: 'both',
    daily_price: '',
    event_price: '',
    calculation_type: 'flat',
    price_per_person_per_day: '',
    price_per_person_event: '',
    minimum_people: '',
    maximum_people: '',
  });

  const [stockPolicy, setStockPolicy] = useState('untracked');
  const [stockQuantity, setStockQuantity] = useState('');
  const [reservedQuantity, setReservedQuantity] = useState(0);
  const [allowBackorder, setAllowBackorder] = useState(false);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, cats, unitList] = await Promise.all([
        fetchProduct(productId),
        fetchCategories().catch(() => []),
        fetchUnits().catch(() => []),
      ]);
      apiClient.get('/supplier/product-profiles').then(r => setProductProfiles(r.data || [])).catch(() => {});
      // Pattern gruplarını yükle (renk/desen seçimi için)
      apiClient.get('/ultra-admin/pattern-groups/public').then(r => setAllPatternGroups(r.data || [])).catch(() => {});
      setForm({
        title: p.title || '', title_tr: p.title_tr || '', title_en: p.title_en || '',
        description: p.description || '',
        description_tr: p.description_tr || '',
        description_en: p.description_en || '',
        main_category_id: p.main_category_id || '', subcategory_id: p.subcategory_id || '',
        category: p.category || '', currency: p.currency || 'USD',
        product_code: p.product_code || '',
        min_order_quantity: p.min_order_quantity != null ? String(p.min_order_quantity) : '',
        max_order_quantity: p.max_order_quantity != null ? String(p.max_order_quantity) : '',
        unit_id: p.unit_id || '', unit: p.unit || '',
        lead_time_days: p.lead_time_days != null ? String(p.lead_time_days) : '',
        is_active: p.is_active !== false,
        requires_supplier_confirmation: !!p.requires_supplier_confirmation,
        min_rental_period_days: p.min_rental_period_days != null ? String(p.min_rental_period_days) : '',
        // Product type
        product_type: p.product_type || 'rental',
        service_pricing_type: p.service_pricing_type || 'fixed',
        items_per_carton: p.items_per_carton != null ? String(p.items_per_carton) : '',
        // Yeni pricing
        pricing_mode_supported: p.pricing_mode_supported || 'both',
        daily_price: p.daily_price != null ? String(p.daily_price) : ((p.pricing?.daily != null) ? String(p.pricing.daily) : ''),
        event_price: p.event_price != null ? String(p.event_price) : ((p.pricing?.event_duration != null) ? String(p.pricing.event_duration) : ''),
        calculation_type: p.calculation_type || 'flat',
        price_per_person_per_day: p.price_per_person_per_day != null ? String(p.price_per_person_per_day) : '',
        price_per_person_event: p.price_per_person_event != null ? String(p.price_per_person_event) : '',
        minimum_people: p.minimum_people != null ? String(p.minimum_people) : '',
        maximum_people: p.maximum_people != null ? String(p.maximum_people) : '',
        // Forklift fields
        forklift_type: p.forklift_type || '',
        forklift_tonnage: p.forklift_tonnage != null ? String(p.forklift_tonnage) : '',
        operating_hours_start: p.operating_hours_start || '08:00',
        operating_hours_end: p.operating_hours_end || '18:00',
        hourly_price: p.hourly_price != null ? String(p.hourly_price) : '',
      });
      setStockPolicy(p.stock_policy || 'untracked');
      setStockQuantity(p.stock_quantity != null ? String(p.stock_quantity) : '');
      setReservedQuantity(p.reserved_quantity || 0);
      setAllowBackorder(!!p.allow_backorder);
      setImages(p.image_objects || []);
      const loadedVariants = (p.variants || []).map(v => ({
        ...v,
        _dirty: false,
        daily_price: v.price_override_daily != null ? String(v.price_override_daily) : (v.price != null ? String(v.price) : ''),
        event_price: v.price_override_event != null ? String(v.price_override_event) : '',
      }));
      setVariants(loadedVariants);
      // Extract variant images from loaded data
      const vImgs = {};
      loadedVariants.forEach(v => { if (v.id) vImgs[v.id] = v.images || []; });
      setVariantImages(vImgs);
      setCategories(cats);
      setUnits(unitList);
    } catch {
      toast.error(tr ? 'Ürün yüklenemedi.' : 'Could not load product.');
      onBack();
    } finally { setLoading(false); }
  }, [productId, onBack]);

  useEffect(() => { load(); }, [load]);

  const selectedCat = categories.find(c => c.id === form.main_category_id);
  const isHR = selectedCat?.name === 'Human Resources';
  const isForklift = form.subcategory_id === FORKLIFT_PROVIDER_SUBCATEGORY_ID || !!form.forklift_type;
  const subcategories = selectedCat?.subcategories || [];

  // Ürün şablonları — tamamen API'den (hardcoded fallback yok)
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [appliedProfile, setAppliedProfile] = useState(null); // Şablon üzerinden uygulanan profil

  const templateOptions = useMemo(() => {
    if (isForklift || !form.subcategory_id) return [];
    const sub = (selectedCat?.subcategories || []).find(s => s.id === form.subcategory_id);
    return sub?.product_templates || [];
  }, [isForklift, form.subcategory_id, selectedCat]);

  useEffect(() => {
    setSelectedTemplate('');
    setAppliedProfile(null);
  }, [form.subcategory_id]);

  // Şablon seçilince ürün adını ve profili otomatik uygula
  const applyTemplate = (val) => {
    setSelectedTemplate(val);
    setAvailablePatternItems([]);
    setSelectedPatternIds(new Set());
    if (!val || val === '__custom') {
      setAppliedProfile(null);
      return;
    }
    setForm(f => ({ ...f, title: val }));
    const sub = (selectedCat?.subcategories || []).find(s => s.id === form.subcategory_id);
    const profileId = (sub?.template_profile_map || {})[val];
    if (profileId) {
      const profile = productProfiles.find(p => p.id === profileId);
      if (profile) {
        setForm(f => ({
          ...f,
          title: val,
          calculation_type:     profile.calculation_type || f.calculation_type,
          service_pricing_type: profile.pricing_mode     || f.service_pricing_type,
        }));
        setAppliedProfile(profile);
        toast.success(tr
          ? `"${profile.name}" profili hesaplama tipini otomatik ayarladı`
          : `Profile "${profile.name}" auto-set the calculation type`);
      }
    } else {
      setAppliedProfile(null);
    }
    // Bu şablona atanmış pattern öğelerini yükle
    const itemIds = (sub?.template_pattern_map || {})[val] || [];
    if (itemIds.length > 0) {
      const items = allPatternGroups.flatMap(g => (g.items || []).filter(i => itemIds.includes(i.id)));
      setAvailablePatternItems(items);
    }
  };

  // HR kategorisi seçilince otomatik olarak "Kişi Başı" + "Kişi Bazlı" hesaplama tipine geç
  useEffect(() => {
    if (isHR) {
      if (form.service_pricing_type !== 'person') set('service_pricing_type', 'person');
      if (form.calculation_type !== 'person_based') set('calculation_type', 'person_based');
    }
  }, [isHR]); // eslint-disable-line

  // Forklift kategorisi seçilince stok politikasını zorla
  useEffect(() => {
    if (isForklift && stockPolicy !== 'strict') setStockPolicy('strict');
  }, [isForklift]); // eslint-disable-line

  // Forklift tipi seçilince ürün adını otomatik doldur (değiştirilebilir)
  useEffect(() => {
    if (!isForklift || !form.forklift_type || form.forklift_type === 'custom') return;
    const preset = FORKLIFT_PRESETS.find(p => p.key === form.forklift_type);
    if (!preset) return;
    const allLabels = FORKLIFT_PRESETS.map(p => p.label);
    if (!form.title || allLabels.includes(form.title)) {
      set('title', preset.label);
    }
  }, [form.forklift_type]); // eslint-disable-line

  // ── Image handlers ──────────────────────────────────────────────────────
  const handleUpload = async (files) => {
    const valid = Array.from(files).filter(f => {
      if (!f.type.startsWith('image/')) { toast.error(tr ? `${f.name}: Görsel olmalı.` : `${f.name}: must be an image.`); return false; }
      if (f.size > 10 * 1024 * 1024) { toast.error(tr ? `${f.name}: Maks. 10 MB.` : `${f.name}: max 10 MB.`); return false; }
      return true;
    });
    const space = 5 - images.length;
    const toUpload = valid.slice(0, space);
    if (!toUpload.length) return;
    if (toUpload.length < valid.length) toast.warning(tr ? `En fazla ${space} görsel daha ekleyebilirsiniz.` : `You can add at most ${space} more image(s).`);
    setUploading(true);
    for (const file of toUpload) {
      try {
        const result = await uploadProductImage(productId, file, p => setUploadPct(p));
        setImages(prev => [...prev, result]);
      } catch (e) {
        toast.error(e.response?.data?.detail || `${file.name} yüklenemedi.`);
      }
    }
    setUploading(false); setUploadPct(0);
  };

  const handleDeleteImage = async (img) => {
    try {
      await deleteImage(img.id);
      setImages(prev => {
        const next = prev.filter(i => i.id !== img.id);
        if (img.is_cover && next.length > 0) next[0] = { ...next[0], is_cover: true };
        return next;
      });
      toast.success(tr ? 'Görsel silindi.' : 'Image deleted.');
    } catch { toast.error(tr ? 'Silinemedi.' : 'Delete failed.'); }
  };

  const handleSetCover = async (img) => {
    try {
      await setCoverImage(img.id);
      setImages(prev => prev.map(i => ({ ...i, is_cover: i.id === img.id })));
    } catch { toast.error(tr ? 'Hata.' : 'Error.'); }
  };

  // ── Drag & drop reorder ─────────────────────────────────────────────────
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragEnter = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    setImages(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = async () => {
    setDragIdx(null);
    try {
      await reorderImages(images.map(i => i.id));
    } catch { toast.error(tr ? 'Sıralama kaydedilemedi.' : 'Reorder save failed.'); }
  };

  // ── Variant handlers ────────────────────────────────────────────────────
  const updateVariantField = (idx, k, val) => {
    setVariants(prev => prev.map((v, i) => i === idx ? { ...v, [k]: val, _dirty: true } : v));
  };
  const addVariant = () => {
    const baseDaily = form.daily_price || '';
    const baseEvent = form.event_price || '';
    const baseStock = form.min_order_quantity || '';
    setVariants(prev => [...prev, { _new: true, _dirty: true, name: '', daily_price: baseDaily, event_price: baseEvent, stock: baseStock, color_id: null, image_file: null, image_preview: null }]);
  };
  const removeVariant = async (idx) => {
    const v = variants[idx];
    if (!v._new && v.id) {
      try { await deleteVariant(v.id); } catch { toast.error(tr ? 'Varyant silinemedi.' : 'Variant delete failed.'); return; }
      // Remove variant images from state
      setVariantImages(prev => { const n = { ...prev }; delete n[v.id]; return n; });
    }
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Variant image handlers ───────────────────────────────────────────────
  const variantFileRefs = useRef({});
  const handleUploadVariantImage = async (variantId, files) => {
    const valid = Array.from(files).filter(f => {
      if (!f.type.startsWith('image/')) { toast.error(tr ? `${f.name}: Görsel olmalı.` : `${f.name}: must be an image.`); return false; }
      if (f.size > 10 * 1024 * 1024) { toast.error(tr ? `${f.name}: Maks. 10 MB.` : `${f.name}: max 10 MB.`); return false; }
      return true;
    });
    const current = variantImages[variantId] || [];
    const space = 5 - current.length;
    const toUpload = valid.slice(0, space);
    if (!toUpload.length) { if (valid.length) toast.warning(tr ? 'Varyant için maksimum 5 görsel yüklenebilir.' : 'Up to 5 images per variant.'); return; }
    setUploadingVariant(variantId);
    for (const file of toUpload) {
      try {
        const result = await uploadVariantImage(variantId, productId, file);
        setVariantImages(prev => ({ ...prev, [variantId]: [...(prev[variantId] || []), result] }));
      } catch (e) {
        toast.error(e.response?.data?.detail || (tr ? `${file.name} yüklenemedi.` : `${file.name} could not be uploaded.`));
      }
    }
    setUploadingVariant(null);
  };
  const handleDeleteVariantImage = async (imageId, variantId) => {
    try {
      await deleteImage(imageId);
      setVariantImages(prev => ({ ...prev, [variantId]: (prev[variantId] || []).filter(i => i.id !== imageId) }));
      toast.success(tr ? 'Görsel silindi.' : 'Image deleted.');
    } catch { toast.error(tr ? 'Silinemedi.' : 'Could not delete.'); }
  };

  // ── Form submit ─────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Ürün adı zorunludur.';
    if (isForklift) {
      if (!form.forklift_type) e.forklift = 'Forklift tipi seçilmelidir.';
      if (!form.hourly_price || isNaN(parseFloat(form.hourly_price))) e.pricing = 'Saatlik ücret girilmelidir.';
    } else {
      const isRentalType = form.product_type === 'rental';
      if (isRentalType || isHR) {
        const hasDailyPrice = form.daily_price !== '' && !isNaN(parseFloat(form.daily_price));
        const hasEventPrice = form.event_price !== '' && !isNaN(parseFloat(form.event_price));
        if (!hasDailyPrice && !hasEventPrice) e.pricing = 'En az bir fiyatlandırma seçeneği girilmelidir.';
      } else {
        const hasPrice = form.daily_price !== '' && !isNaN(parseFloat(form.daily_price));
        if (!hasPrice) e.pricing = form.product_type === 'for_sale' ? 'Satış fiyatı girilmelidir.' : 'Hizmet fiyatı girilmelidir.';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPricingPayload = () => {
    const obj = {};
    const entries = { daily: form.daily_price, event_duration: form.event_price };
    Object.entries(entries).forEach(([k, v]) => {
      if (v !== '' && !isNaN(parseFloat(v))) obj[k] = parseFloat(v);
    });
    return Object.keys(obj).length > 0 ? obj : null;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const f = (v) => (v !== '' && !isNaN(parseFloat(v))) ? parseFloat(v) : null;
      const i = (v) => (v !== '' && !isNaN(parseInt(v))) ? parseInt(v) : null;
      const hourlyPrice = isForklift ? f(form.hourly_price) : null;
      const isRental = form.product_type === 'rental';
      const dailyPrice  = isForklift ? null : f(form.daily_price);
      const eventPrice  = isForklift ? null : ((isRental || isHR) ? f(form.event_price) : null);
      const legacyPrice = hourlyPrice || dailyPrice || eventPrice || 0;

      const payload = {
        title: form.title_tr || form.title_en || form.title || '',
        title_tr: form.title_tr || '',
        title_en: form.title_en || '',
        description: form.description,
        description_tr: form.description_tr || '',
        description_en: form.description_en || '',
        category: form.category,
        main_category_id: form.main_category_id || null,
        subcategory_id: form.subcategory_id || null,
        currency: form.currency,
        product_code: form.product_code || null,
        min_order_quantity: i(form.min_order_quantity),
        max_order_quantity: i(form.max_order_quantity),
        lead_time_days: i(form.lead_time_days),
        min_rental_period_days: isRental ? i(form.min_rental_period_days) : null,
        unit_id: form.unit_id || null,
        is_active: form.is_active,
        requires_supplier_confirmation: !!form.requires_supplier_confirmation,
        product_type: isForklift ? 'rental' : form.product_type,
        items_per_carton: form.calculation_type === 'carton_based' ? i(form.items_per_carton) : null,
        pricing_mode_supported: isForklift ? 'hourly' : ((isRental || form.service_pricing_type === 'daily' || isHR) ? 'both' : 'daily'),
        daily_price: dailyPrice,
        event_price: eventPrice,
        hourly_price: hourlyPrice,
        calculation_type: isForklift ? 'flat' : (form.calculation_type || 'flat'),
        price_per_person_per_day: f(form.price_per_person_per_day),
        price_per_person_event: (isRental || isHR) ? f(form.price_per_person_event) : null,
        minimum_people: i(form.minimum_people),
        maximum_people: i(form.maximum_people),
        price: legacyPrice,
        pricing: { daily: dailyPrice, event_duration: eventPrice },
        stock_policy: stockPolicy,
        stock_quantity: stockPolicy !== 'untracked' && stockQuantity !== '' ? parseInt(stockQuantity) : null,
        allow_backorder: allowBackorder,
        // Forklift specific
        forklift_type: isForklift ? form.forklift_type : null,
        forklift_tonnage: (isForklift && form.forklift_type === 'custom') ? f(form.forklift_tonnage) : null,
        operating_hours_start: isForklift ? (form.operating_hours_start || '08:00') : null,
        operating_hours_end: isForklift ? (form.operating_hours_end || '18:00') : null,
        service_pricing_type: isForklift ? 'hourly' : form.service_pricing_type,
      };
      await updateProduct(productId, payload);

      // Save variants
      for (const v of variants) {
        if (!v._dirty) continue;
        if (!v.name?.trim()) continue;
        const data = {
          name: v.name,
          price: v.daily_price ? parseFloat(v.daily_price) : (v.price ? parseFloat(v.price) : null),
          price_override_daily: v.daily_price ? parseFloat(v.daily_price) : null,
          price_override_event: v.event_price ? parseFloat(v.event_price) : null,
          sku: v.sku || null,
          stock: v.stock ? parseInt(v.stock) : null,
          color_id: v.color_id || null,
        };
        if (v._new) {
          const createdV = await createVariant(productId, data);
          if (v.image_file && createdV?.id) {
            await uploadVariantImage(createdV.id, productId, v.image_file).catch(() => {});
          }
        } else if (v.id) {
          await updateVariant(v.id, data);
          if (v.image_file) {
            await uploadVariantImage(v.id, productId, v.image_file).catch(() => {});
          }
        }
      }

      toast.success(tr ? 'Ürün güncellendi.' : 'Product updated.');
      onSaved(productId);
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Hata oluştu.' : 'An error occurred.'));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Ürünü Düzenle</h1>
            {form.product_type && (
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                form.product_type === 'rental'   ? 'bg-emerald-100 text-emerald-700' :
                form.product_type === 'service'  ? 'bg-purple-100 text-purple-700'  :
                                                   'bg-blue-100 text-blue-700'
              }`}>
                {form.product_type === 'rental' ? 'Kiralık' : form.product_type === 'service' ? 'Hizmet' : 'Satılık'}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400 truncate max-w-xs">{form.title}</p>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 divide-y divide-gray-50">

        {/* 1 - Category */}
        <div className="p-6 space-y-4">
          <SH step="1" title="Kategori" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Ana Kategori</Label>
              <select value={form.main_category_id} onChange={e => {
                const cat = categories.find(c => c.id === e.target.value);
                set('main_category_id', e.target.value); set('category', cat?.name || ''); set('subcategory_id', '');
              }} className={SEL}>
                <option value="">—</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Alt Kategori</Label>
              <select value={form.subcategory_id} onChange={e => set('subcategory_id', e.target.value)}
                disabled={!form.main_category_id || !subcategories.length} className={`${SEL} disabled:opacity-40`}>
                <option value="">—</option>
                {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Ürün Şablonu — alt kategori seçilince göster (forklift hariç) */}
          {templateOptions.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Ürün Şablonu Seç</Label>
              <select value={selectedTemplate} onChange={e => applyTemplate(e.target.value)} className={SEL}>
                <option value="">— Şablon seç (opsiyonel)</option>
                {templateOptions.map(t => <option key={t} value={t}>{t}</option>)}
                <option value="__custom">Kendiniz Yazın</option>
              </select>
              {selectedTemplate && selectedTemplate !== '__custom' && (
                <p className="text-xs text-blue-500 bg-blue-50 px-3 py-1.5 rounded-lg">
                  Ürün adı <span className="font-semibold">"{selectedTemplate}"</span> olarak dolduruldu — aşağıdan değiştirebilirsiniz.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">
                Min. Sipariş{form.calculation_type === 'carton_based' ? ' (koli)' : ' (adet)'}
              </Label>
              <Input type="number" value={form.min_order_quantity}
                onChange={e => set('min_order_quantity', e.target.value)}
                className="rounded-xl" min="1"
                placeholder={form.calculation_type === 'carton_based' ? 'ör. 2' : 'ör. 1'} />
              {form.calculation_type === 'carton_based' && form.items_per_carton && form.min_order_quantity && (
                <p className="text-[11px] text-amber-600">
                  = {(parseInt(form.min_order_quantity) || 0) * (parseInt(form.items_per_carton) || 1)} adet
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">
                Maks. Sipariş{form.calculation_type === 'carton_based' ? ' (koli)' : ' (adet)'}
              </Label>
              <Input type="number" value={form.max_order_quantity}
                onChange={e => set('max_order_quantity', e.target.value)}
                className="rounded-xl" min="1"
                placeholder="— (sınırsız)" />
              {form.calculation_type === 'carton_based' && form.items_per_carton && form.max_order_quantity && (
                <p className="text-[11px] text-amber-600">
                  = {(parseInt(form.max_order_quantity) || 0) * (parseInt(form.items_per_carton) || 1)} adet
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Birim</Label>
              <select value={form.unit_id} onChange={e => {
                const u = units.find(u => u.id === e.target.value);
                set('unit_id', e.target.value); set('unit', u?.code || '');
              }} className={SEL}>
                <option value="">—</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* 2.5 - Forklift Tipi (Logistic Company kategorisinde gösterilir) */}
        {isForklift && (
          <div className="p-6 space-y-4 bg-blue-50/40 border-l-4 border-blue-400">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-lg uppercase tracking-wide">Forklift Tipi</span>
              <span className="text-xs text-gray-400">Sadece bir tip seçilebilir</span>
            </div>
            {errors.forklift && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                <AlertCircle className="h-4 w-4 shrink-0" />{errors.forklift}
              </div>
            )}
            <div className="space-y-2">
              {FORKLIFT_PRESETS.map(preset => (
                <button key={preset.key} type="button"
                  onClick={() => { set('forklift_type', preset.key); set('forklift_tonnage', ''); }}
                  className={`w-full flex items-center justify-between rounded-xl px-4 py-2.5 text-sm border-2 transition-all ${
                    form.forklift_type === preset.key
                      ? 'border-blue-500 bg-blue-100 text-blue-800 font-semibold'
                      : 'border-gray-100 bg-white text-gray-700 hover:border-blue-200'
                  }`}>
                  {preset.label}
                  {form.forklift_type === preset.key && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                </button>
              ))}
              <button type="button"
                onClick={() => set('forklift_type', 'custom')}
                className={`w-full flex items-center justify-between rounded-xl px-4 py-2.5 text-sm border-2 transition-all ${
                  form.forklift_type === 'custom'
                    ? 'border-blue-500 bg-blue-100 text-blue-800 font-semibold'
                    : 'border-gray-100 bg-white text-gray-700 hover:border-blue-200'
                }`}>
                Özel Tonaj (Kendiniz Girin)
                {form.forklift_type === 'custom' && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
              </button>
            </div>
            {form.forklift_type === 'custom' && (
              <div className="space-y-2">
                <Label className="text-xs text-gray-500 uppercase">Tonaj</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="number" step="0.5" min="0.5" value={form.forklift_tonnage}
                    onChange={e => set('forklift_tonnage', e.target.value)}
                    className="rounded-xl max-w-[140px]" placeholder="ör. 10" />
                  <span className="text-sm text-gray-400">Ton</span>
                  <button type="button"
                    data-testid="apply-custom-tonnage-btn"
                    onClick={() => {
                      if (!form.forklift_tonnage) return;
                      const label = tr ? `Özel Forklift ${form.forklift_tonnage} Ton` : `Custom Forklift ${form.forklift_tonnage} Ton`;
                      set('title', label);
                      toast.success(tr ? `Ürün adı "${label}" olarak güncellendi` : `Product name updated to "${label}"`);
                    }}
                    disabled={!form.forklift_tonnage}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold transition-all shadow-sm">
                    <Check className="h-3.5 w-3.5" /> Uygula
                  </button>
                </div>
                {form.forklift_tonnage && (
                  <p className="text-xs text-blue-500 bg-blue-50 px-3 py-1.5 rounded-lg">
                    Önizleme: <span className="font-semibold">Özel Forklift {form.forklift_tonnage} Ton</span>
                    <span className="text-gray-400 ml-1">— Ürün adına uygulamak için "Uygula"ya tıklayın</span>
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Çalışma Saatleri</Label>
              <div className="flex items-center gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-400">Başlangıç</Label>
                  <Input type="time" value={form.operating_hours_start || '08:00'}
                    onChange={e => set('operating_hours_start', e.target.value)} className="rounded-xl w-32" />
                </div>
                <span className="text-gray-300 pt-5">—</span>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-400">Bitiş</Label>
                  <Input type="time" value={form.operating_hours_end || '18:00'}
                    onChange={e => set('operating_hours_end', e.target.value)} className="rounded-xl w-32" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2 - Temel Bilgiler (Kategori ve spesifik ürün seçiminden sonra) */}
        <div className="p-6 space-y-4">
          <SH step="2" title="Temel Bilgiler" desc={
            isForklift && form.forklift_type ? 'Seçilen forklift tipine göre otomatik dolduruldu, değiştirebilirsiniz.' : undefined
          } />
          <div className="grid grid-cols-2 gap-4">
            {/* Ürün Adı TR + EN */}
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇹🇷</span> Ürün Adı (TR) <span className="text-red-500">*</span>
                </Label>
                <Input value={form.title_tr} onChange={e => { set('title_tr', e.target.value); set('title', e.target.value || form.title_en); }}
                  data-testid="edit-product-title-tr-input"
                  className={`rounded-xl ${errors.title ? 'border-red-400' : ''}`}
                  placeholder="Türkçe ürün adı..." />
                {errors.title && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.title}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇬🇧</span> Product Name (EN)
                </Label>
                <Input value={form.title_en} onChange={e => { set('title_en', e.target.value); if (!form.title_tr) set('title', e.target.value); }}
                  data-testid="edit-product-title-en-input"
                  className="rounded-xl"
                  placeholder="English product name..." />
              </div>
            </div>
            <div className="col-span-2 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇹🇷</span> Açıklama (Türkçe)
                </Label>
                <textarea rows={4} value={form.description_tr} onChange={e => set('description_tr', e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                  placeholder="Türkçe ürün açıklaması... (satır sonları korunur)" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                  <span className="text-lg">🇬🇧</span> Description (English)
                </Label>
                <textarea rows={4} value={form.description_en} onChange={e => set('description_en', e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                  placeholder="English product description... (line breaks preserved)" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                <Tag className="h-3 w-3" /> Ürün Kodu
              </Label>
              <Input value={form.product_code} onChange={e => set('product_code', e.target.value)}
                data-testid="edit-product-code-input" className="rounded-xl font-mono" placeholder="YK-XXXX" />
            </div>
            {form.product_type === 'rental' && !isForklift && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Min. Kiralama Süresi (gün)</Label>
                <Input type="number" value={form.min_rental_period_days}
                  onChange={e => set('min_rental_period_days', e.target.value)}
                  className="rounded-xl" min="1" placeholder="—" />
              </div>
            )}
          </div>

          {/* Renk/Desen Seçimi — şablon için atanmış pattern öğeleri */}
          {availablePatternItems.length > 0 && (
            <div className="space-y-2 pt-1 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1.5">
                  <Palette className="h-3 w-3 text-purple-500" /> Renk/Desen Seçimi
                  <span className="text-[10px] font-normal text-gray-400 normal-case ml-1">Bu ürün için mevcut renkler</span>
                </Label>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedPatternIds(new Set(availablePatternItems.map(i => i.id)))}
                    className="text-[10px] text-purple-600 hover:text-purple-800">Tümünü Seç</button>
                  <span className="text-gray-300 text-[10px]">|</span>
                  <button onClick={() => setSelectedPatternIds(new Set())}
                    className="text-[10px] text-gray-400 hover:text-gray-600">Temizle</button>
                  {selectedPatternIds.size > 0 && (
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      {selectedPatternIds.size} seçili
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {availablePatternItems.map(item => {
                  const sel = selectedPatternIds.has(item.id);
                  return (
                    <button
                      key={item.id} type="button" title={item.name}
                      onClick={() => setSelectedPatternIds(prev => { const n = new Set(prev); sel ? n.delete(item.id) : n.add(item.id); return n; })}
                      className="group relative flex flex-col items-center gap-1 transition-all focus:outline-none"
                    >
                      <div className={`w-10 h-10 rounded-xl overflow-hidden border-2 transition-all ${sel ? 'border-purple-500 ring-2 ring-purple-200 scale-110' : 'border-gray-200 hover:border-purple-300'}`}>
                        {item.image_url
                          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />}
                        {sel && <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-purple-700" strokeWidth={3} /></div>}
                      </div>
                      <span className={`text-[9px] max-w-[40px] truncate ${sel ? 'text-purple-700 font-semibold' : 'text-gray-400'}`}>{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* 3 - Pricing (conditional per product type) */}
        <div className="p-6 space-y-4">
          <SH step="3" title={
            isForklift              ? 'Saatlik Ücret' :
            form.product_type === 'service'  ? 'Fiyatlandırma — Hizmet' :
            form.product_type === 'for_sale' ? 'Fiyatlandırma — Satılık' :
                                               'Fiyatlandırma'
          } />
          {errors.pricing && (
            <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
              <AlertCircle className="h-4 w-4 shrink-0" />{errors.pricing}
            </div>
          )}
          {/* Common: Currency + Lead time + Active toggle */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="space-y-1.5 w-32">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Para Birimi</Label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className={SEL}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Tedarik Süresi</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => set('lead_time_days', form.lead_time_days === '0' ? '' : '0')}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all whitespace-nowrap ${
                    form.lead_time_days === '0'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-600'
                  }`}
                >
                  Aynı Gün
                </button>
                <Input
                  type="number"
                  value={form.lead_time_days === '0' ? '' : form.lead_time_days}
                  onChange={e => set('lead_time_days', e.target.value)}
                  disabled={form.lead_time_days === '0'}
                  placeholder="Gün sayısı"
                  className="rounded-xl max-w-[200px] disabled:opacity-40"
                  min="1"
                />
                {form.lead_time_days && form.lead_time_days !== '0' && (
                  <span className="text-xs text-gray-400 whitespace-nowrap">gün</span>
                )}
              </div>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <button type="button" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  className={`w-11 h-6 rounded-full transition-colors relative ${form.is_active ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                  <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-all ${form.is_active ? 'left-5' : 'left-0.5'}`} />
                </button>
                <span className="text-sm text-gray-600">Aktif</span>
              </label>
            </div>
          </div>

          {/* ── Manual review toggle ──────────────────────────────────────── */}
          <div className="bg-amber-50/60 rounded-2xl p-4 flex items-start gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, requires_supplier_confirmation: !f.requires_supplier_confirmation }))}
              data-testid="product-requires-confirmation-toggle"
              className={`w-11 h-6 rounded-full transition-colors relative shrink-0 mt-0.5 ${form.requires_supplier_confirmation ? 'bg-amber-500' : 'bg-gray-200'}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-all ${form.requires_supplier_confirmation ? 'left-5' : 'left-0.5'}`} />
            </button>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                Manuel onay gerektirsin
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {form.requires_supplier_confirmation
                  ? 'Müşteri ödeme yapsa bile sipariş "Onay Bekliyor" olarak kalır — siz manuel olarak onaylayana kadar. Özel tasarım/imalat gerektiren ürünler için uygundur.'
                  : 'Ödeme yapıldığı anda sipariş otomatik onaylanır ve "Ödendi" olarak işaretlenir. Standart katalog ürünleri için önerilir.'}
              </div>
            </div>
          </div>

          {/* ── FORKLIFT HOURLY PRICE ── */}
          {isForklift && (
            <div className="bg-blue-50/60 rounded-2xl p-4 space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Saatlik Ücret</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                <Input type="number" value={form.hourly_price}
                  onChange={e => set('hourly_price', e.target.value)}
                  data-testid="hourly-price-input"
                  className="rounded-xl pl-12" min="0" placeholder="0.00" />
              </div>
            </div>
          )}

          {/* ── RENTAL PRICING ── */}
          {!isForklift && form.product_type === 'rental' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50/60 rounded-2xl p-4 space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">Günlük Kiralama Fiyatı</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                    <Input type="number" value={form.daily_price}
                      onChange={e => set('daily_price', e.target.value)}
                      data-testid="edit-daily-price"
                      className="rounded-xl pl-12" min="0" placeholder="0.00" />
                  </div>
                  <p className="text-[10px] text-emerald-600">Müşteri tarihleri seçer → gün × fiyat</p>
                </div>
                <div className="bg-amber-50/60 rounded-2xl p-4 space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">Etkinlik Fiyatı (Sabit)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                    <Input type="number" value={form.event_price}
                      onChange={e => set('event_price', e.target.value)}
                      data-testid="edit-event-price"
                      className="rounded-xl pl-12" min="0" placeholder="0.00" />
                  </div>
                  <p className="text-[10px] text-amber-600">Tüm etkinlik boyunca tek fiyat</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hesaplama Tipi</Label>
                {appliedProfile && (
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                    <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                    <span className="text-xs text-indigo-700">
                      <span className="font-semibold">"{appliedProfile.name}"</span> profilinden otomatik ayarlandı
                      {appliedProfile.formula && (
                        <span className="ml-1 text-indigo-500">— {appliedProfile.formula}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className={`grid grid-cols-1 gap-2 ${appliedProfile ? 'pointer-events-none opacity-60' : ''}`}>
                  {[
                    {key:'flat',             label:'Sabit (flat)',        desc:'Direkt fiyat'},
                    {key:'quantity_based',   label:'Adet Bazlı',          desc:'Adet × fiyat'},
                    {key:'person_based',     label:'Kişi Bazlı',          desc:'Kişi × fiyat'},
                    {key:'quantity_and_days',label:'Adet × Gün',          desc:'Adet × gün × fiyat'},
                    {key:'person_and_days',  label:'Kişi × Gün',          desc:'Kişi × gün × fiyat'},
                  ].map(opt => (
                    <button key={opt.key}
                      data-testid={`calc-type-${opt.key}`}
                      onClick={() => set('calculation_type', opt.key)}
                      className={`rounded-xl px-3 py-2 text-left text-sm border-2 transition-all ${
                        form.calculation_type === opt.key ? 'border-amber-500 bg-amber-50' : 'border-gray-100 hover:border-gray-200'
                      }`}>
                      <span className={`font-semibold ${form.calculation_type === opt.key ? 'text-amber-700' : 'text-gray-700'}`}>{opt.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              {(form.calculation_type === 'person_based' || form.calculation_type === 'person_and_days') && (
                <div className="space-y-3 bg-purple-50/50 rounded-2xl p-4 border border-purple-100">
                  <p className="text-xs font-semibold text-purple-600 uppercase flex items-center gap-1.5">
                    <Tag className="h-3 w-3" /> Kişi Bazlı Fiyatlandırma
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {(form.pricing_mode_supported === 'daily' || form.pricing_mode_supported === 'both') && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-gray-500 uppercase">Kişi Başı / Gün</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{form.currency}</span>
                          <Input type="number" value={form.price_per_person_per_day} onChange={e => set('price_per_person_per_day', e.target.value)} className="rounded-xl pl-10" min="0" placeholder="0.00" />
                        </div>
                      </div>
                    )}
                    {(form.pricing_mode_supported === 'event' || form.pricing_mode_supported === 'both') && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-gray-500 uppercase">Kişi Başı / Etkinlik</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{form.currency}</span>
                          <Input type="number" value={form.price_per_person_event} onChange={e => set('price_per_person_event', e.target.value)} className="rounded-xl pl-10" min="0" placeholder="0.00" />
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-500 uppercase">Min. Kişi</Label>
                      <Input type="number" value={form.minimum_people} onChange={e => set('minimum_people', e.target.value)} className="rounded-xl" min="1" placeholder="1" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-500 uppercase">Maks. Kişi</Label>
                      <Input type="number" value={form.maximum_people} onChange={e => set('maximum_people', e.target.value)} className="rounded-xl" min="1" placeholder="—" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── SERVICE PRICING ── */}
          {!isForklift && form.product_type === 'service' && (
            <>
              {/* HR kategorisi: sadece Kişi Başı göster */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hizmet Fiyatlandırma Tipi</Label>
                {isHR ? (
                  <div>
                    <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl border-2 border-purple-500 bg-purple-50 text-purple-700">
                      Kişi Başı
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {[
                      {key:'fixed',  label:'Sabit Ücret'},
                      {key:'hourly', label:'Saatlik'},
                      {key:'daily',  label:'Günlük'},
                      {key:'person', label:'Kişi Başı'},
                      {key:'custom', label:'Özel'},
                    ].map(opt => (
                      <button key={opt.key} onClick={() => set('service_pricing_type', opt.key)}
                        className={`rounded-xl px-3 py-1.5 text-xs font-semibold border-2 transition-all ${form.service_pricing_type===opt.key?'border-purple-500 bg-purple-50 text-purple-700':'border-gray-100 text-gray-500 hover:border-gray-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Günlük VEYA HR → iki fiyat yan yana */}
              {(form.service_pricing_type === 'daily' || isHR) ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-50/60 rounded-2xl p-4 space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase">
                      {isHR ? 'Günlük Fiyat (Kişi Başı)' : 'Günlük Fiyat'}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                      <Input type="number" value={form.daily_price}
                        onChange={e => set('daily_price', e.target.value)}
                        data-testid="edit-daily-price"
                        className="rounded-xl pl-12" min="0" placeholder="0.00" />
                    </div>
                    <p className="text-[10px] text-emerald-600">
                      {isHR ? 'Kişi sayısı × günlük fiyat × gün' : 'Müşteri tarihleri seçer → gün × fiyat'}
                    </p>
                  </div>
                  <div className="bg-amber-50/60 rounded-2xl p-4 space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase">Etkinlik Fiyatı (Sabit)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                      <Input type="number" value={form.event_price}
                        onChange={e => set('event_price', e.target.value)}
                        data-testid="edit-event-price"
                        className="rounded-xl pl-12" min="0" placeholder="0.00" />
                    </div>
                    <p className="text-[10px] text-amber-600">Tüm etkinlik boyunca tek fiyat</p>
                  </div>
                </div>
              ) : (
                <div className="bg-purple-50/50 rounded-2xl p-4 space-y-1.5 border border-purple-100">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">Hizmet Fiyatı</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                    <Input type="number" value={form.daily_price}
                      onChange={e => set('daily_price', e.target.value)}
                      data-testid="edit-service-price"
                      className="rounded-xl pl-12" min="0" placeholder="0.00" />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hesaplama Tipi</Label>
                {isHR ? (
                  <div>
                    <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl border-2 border-purple-500 bg-purple-50 text-purple-700">
                      Kişi Bazlı
                    </span>
                  </div>
                ) : (
                  <>
                    {appliedProfile && (
                      <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                        <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                        <span className="text-xs text-indigo-700">
                          <span className="font-semibold">"{appliedProfile.name}"</span> profilinden otomatik ayarlandı
                          {appliedProfile.formula && (
                            <span className="ml-1 text-indigo-500">— {appliedProfile.formula}</span>
                          )}
                        </span>
                      </div>
                    )}
                    <div className={`grid grid-cols-1 gap-2 ${appliedProfile ? 'pointer-events-none opacity-60' : ''}`}>
                      {[
                        {key:'flat',          label:'Sabit',        desc:'Tek seferlik sabit ücret'},
                        {key:'quantity_based',label:'Adet Bazlı',   desc:'Adet × fiyat'},
                        {key:'person_based',  label:'Kişi Bazlı',   desc:'Kişi sayısı × fiyat'},
                      ].map(opt => (
                        <button key={opt.key}
                          data-testid={`calc-type-${opt.key}`}
                          onClick={() => set('calculation_type', opt.key)}
                          className={`rounded-xl px-3 py-2 text-left text-sm border-2 transition-all ${
                            form.calculation_type === opt.key ? 'border-purple-500 bg-purple-50' : 'border-gray-100 hover:border-gray-200'
                          }`}>
                          <span className={`font-semibold ${form.calculation_type === opt.key ? 'text-purple-700' : 'text-gray-700'}`}>{opt.label}</span>
                          <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {(form.calculation_type === 'person_based' || isHR) && (
                <div className="bg-purple-50/50 rounded-2xl p-4 space-y-3 border border-purple-100">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500 uppercase">Min. Kişi</Label>
                      <Input type="number" value={form.minimum_people} onChange={e => set('minimum_people', e.target.value)} className="rounded-xl" min="1" placeholder="1" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500 uppercase">Maks. Kişi</Label>
                      <Input type="number" value={form.maximum_people} onChange={e => set('maximum_people', e.target.value)} className="rounded-xl" min="1" placeholder="—" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── FOR SALE PRICING ── */}
          {!isForklift && form.product_type === 'for_sale' && (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 uppercase">Hesaplama Tipi</Label>
                {appliedProfile && (
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                    <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                    <span className="text-xs text-indigo-700">
                      <span className="font-semibold">"{appliedProfile.name}"</span> profilinden otomatik ayarlandı
                      {appliedProfile.formula && (
                        <span className="ml-1 text-indigo-500">— {appliedProfile.formula}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className={`grid grid-cols-1 gap-2 ${appliedProfile ? 'pointer-events-none opacity-60' : ''}`}>
                  {[
                    {key:'quantity_based', label:'Adet Bazlı',      desc:'Adet × birim fiyat'},
                    {key:'carton_based',   label:'Koli/Paket Bazlı', desc:'Koli sayısı × koli fiyatı'},
                  ].map(opt => (
                    <button key={opt.key}
                      data-testid={`calc-type-${opt.key}`}
                      onClick={() => set('calculation_type', opt.key)}
                      className={`rounded-xl px-3 py-2 text-left text-sm border-2 transition-all ${
                        form.calculation_type === opt.key ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'
                      }`}>
                      <span className={`font-semibold ${form.calculation_type === opt.key ? 'text-blue-700' : 'text-gray-700'}`}>{opt.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {form.calculation_type === 'carton_based' && (
                <div className="bg-orange-50/60 rounded-2xl p-4 space-y-1.5 border border-orange-100">
                  <Label className="text-xs font-semibold text-gray-500 uppercase">
                    Bir Koli / Pakette Kaç Adet Var?
                  </Label>
                  <Input
                    type="number"
                    value={form.items_per_carton}
                    onChange={e => set('items_per_carton', e.target.value)}
                    data-testid="edit-items-per-carton"
                    className="rounded-xl" min="1" placeholder="Örn: 12"
                  />
                  <p className="text-xs text-gray-400">Her bir koli/pakette bulunan birim sayısı</p>
                </div>
              )}

              <div className={`rounded-2xl p-4 space-y-1.5 border ${form.calculation_type === 'carton_based' ? 'bg-orange-50/30 border-orange-100' : 'bg-blue-50/50 border-blue-100'}`}>
                <Label className="text-xs font-semibold text-gray-500 uppercase">
                  {form.calculation_type === 'carton_based' ? 'Koli / Paket Fiyatı' : 'Satış Fiyatı (Birim)'}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{form.currency}</span>
                  <Input type="number" value={form.daily_price}
                    onChange={e => set('daily_price', e.target.value)}
                    data-testid="edit-sale-price"
                    className="rounded-xl pl-12" min="0" placeholder="0.00" />
                </div>
              </div>
            </>
          )}
        </div>

        {/* 4 - Stock Management */}
        <div className="p-6 space-y-4">
          <SH step="4" title="Stok Yönetimi" />
          <div className="space-y-2">
            {STOCK_POLICIES.map(pol => (
              <label key={pol.value}
                data-testid={`edit-stock-policy-${pol.value}`}
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  stockPolicy === pol.value ? 'border-amber-400 bg-amber-50' : 'border-gray-100 hover:border-gray-200'
                }`}>
                <input type="radio" name="edit_stock_policy" value={pol.value}
                  checked={stockPolicy === pol.value}
                  onChange={() => setStockPolicy(pol.value)}
                  className="mt-0.5 accent-amber-500" />
                <div>
                  <span className="text-sm font-semibold text-gray-800">{pol.label}</span>
                  <p className="text-xs text-gray-400">{pol.desc}</p>
                </div>
              </label>
            ))}
          </div>
          {stockPolicy !== 'untracked' && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase">
                  {isForklift ? 'Filo Büyüklüğü (Araç Sayısı)' : 'Stok Miktarı'}
                </Label>
                <Input type="number" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)}
                  data-testid="edit-stock-quantity-input"
                  className="rounded-xl" min="0" placeholder="0" />
                {/* Rezerve / Müsait özeti */}
                {stockQuantity !== '' && (
                  <div className="flex gap-2 pt-1">
                    <span className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-lg">
                      <span className="font-semibold">{reservedQuantity}</span>
                      <span className="text-amber-500">rezerve</span>
                    </span>
                    <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-semibold ${
                      Math.max(0, (parseInt(stockQuantity) || 0) - reservedQuantity) === 0
                        ? 'bg-red-50 text-red-600'
                        : Math.max(0, (parseInt(stockQuantity) || 0) - reservedQuantity) <= 5
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-emerald-50 text-emerald-700'
                    }`}>
                      {Math.max(0, (parseInt(stockQuantity) || 0) - reservedQuantity)}
                      <span className="font-normal opacity-75 ml-0.5">müsait</span>
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button type="button" onClick={() => setAllowBackorder(!allowBackorder)}
                    className={`w-11 h-6 rounded-full transition-colors relative ${allowBackorder ? 'bg-amber-500' : 'bg-gray-200'}`}>
                    <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform ${allowBackorder ? 'translate-x-5.5 left-0.5' : 'left-0.5'}`} />
                  </button>
                  <span className="text-sm text-gray-600">Ön sipariş izni</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* 5 - Images */}
        <div className="p-6 space-y-4">
          <SH step="5" title={`Görseller (${images.length}/5)`} />
          <p className="text-xs text-gray-400">Sürükleyerek sıralayın. ★ ile kapak görselini belirleyin.</p>
          <div className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <span className="text-[11px] text-amber-700 font-medium">
              Önerilen: 800×800 px · JPG/PNG/WebP · Maks. 10 MB/görsel · En fazla 5 görsel
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {images.map((img, i) => (
              <DraggableImage
                key={img.id}
                img={img}
                index={i}
                dragging={dragIdx === i}
                onDragStart={handleDragStart}
                onDragEnter={handleDragEnter}
                onDragEnd={handleDragEnd}
                onCover={() => handleSetCover(img)}
                onDelete={() => handleDeleteImage(img)}
              />
            ))}
            {images.length < 5 && (
              <>
                <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                  onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-24 w-24 rounded-xl border-2 border-dashed border-amber-200 flex flex-col items-center justify-center text-amber-400 hover:bg-amber-50 transition-colors gap-1">
                  {uploading
                    ? <><Loader2 className="h-5 w-5 animate-spin" /><span className="text-[10px]">{uploadPct}%</span></>
                    : <><Upload className="h-5 w-5" /><span className="text-[10px]">Ekle</span></>}
                </button>
              </>
            )}
          </div>
        </div>

        {/* 6 - Variants */}
        <div className="p-6 space-y-4">
          <SH step="6" title="Varyantlar" desc="Farklı renk veya boyut seçenekleri ekleyin. Ana üründen fiyat ve stok otomatik aktarılır." />
          {variants.length > 0 && (
            <div className="space-y-3">
              {variants.map((v, i) => {
                const vid = v.id || null;
                const imgs = vid ? (variantImages[vid] || []) : [];
                const isExpanded = expandedVariant === vid;
                const isUploading = uploadingVariant === vid;
                const showDual = form.product_type === 'rental' || isHR || (form.product_type === 'service' && form.service_pricing_type === 'daily');
                const colorItem = v.color_id ? availablePatternItems.find(p => p.id === v.color_id) : null;
                return (
                  <div key={v.id || `new_${i}`} className="bg-gray-50 border border-gray-100 rounded-2xl p-4 space-y-3">
                    {/* Row 1: Badge + Color + Name + Image + Delete */}
                    <div className="flex items-center gap-3">
                      <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>

                      {/* Color picker — from ALL template pattern items */}
                      {availablePatternItems.length > 0 && (
                        <div className="relative">
                          <button type="button"
                            onClick={() => setOpenColorPicker(openColorPicker === i ? null : i)}
                            className={`h-9 w-9 rounded-xl border-2 overflow-hidden transition-all flex items-center justify-center ${colorItem ? 'border-purple-400' : 'border-dashed border-gray-300 hover:border-purple-300'}`}
                            title={colorItem ? colorItem.name : 'Renk seç'}>
                            {colorItem?.image_url
                              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${colorItem.image_url}`} className="w-full h-full object-cover" alt="" />
                              : <Palette className="h-4 w-4 text-gray-300" />}
                          </button>
                          {openColorPicker === i && (
                            <div className="absolute left-0 top-11 z-30 bg-white rounded-2xl shadow-xl border border-gray-100 p-3 flex flex-wrap gap-1.5 w-52">
                              <button type="button" onClick={() => { updateVariantField(i, 'color_id', null); setOpenColorPicker(null); }}
                                className="text-[10px] text-gray-400 hover:text-red-500 w-full text-left px-1 pb-2 border-b border-gray-100 mb-1">Rengi Kaldır</button>
                              {availablePatternItems.map(ci => (
                                <button key={ci.id} type="button" title={ci.name}
                                  onClick={() => {
                                    updateVariantField(i, 'color_id', ci.id);
                                    updateVariantField(i, 'name', ci.name);
                                    setOpenColorPicker(null);
                                  }}
                                  className={`h-9 w-9 rounded-lg overflow-hidden border-2 transition-all ${v.color_id === ci.id ? 'border-purple-500 scale-110' : 'border-gray-200 hover:border-purple-300'}`}>
                                  {ci.image_url
                                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}${ci.image_url}`} className="w-full h-full object-cover" alt={ci.name} />
                                    : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><span className="text-[8px] text-gray-400">{ci.name?.slice(0,3)}</span></div>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <Input value={v.name}
                        onChange={e => updateVariantField(i, 'name', e.target.value)}
                        placeholder="Varyant adı (ör: Mavi, XL...)" className="rounded-xl flex-1" />

                      {/* Image: saved variant → expand panel; new variant → inline upload */}
                      {vid ? (
                        <button
                          title="Varyant görselleri"
                          onClick={() => setExpandedVariant(isExpanded ? null : vid)}
                          className={`h-9 w-9 rounded-xl border-2 flex items-center justify-center transition-all relative flex-shrink-0 ${imgs.length > 0 ? 'border-amber-400 text-amber-500 bg-amber-50' : 'border-dashed border-gray-200 hover:border-amber-300 text-gray-300'}`}>
                          <Image className="h-4 w-4" />
                          {imgs.length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                              {imgs.length}
                            </span>
                          )}
                        </button>
                      ) : (
                        <label className="cursor-pointer flex-shrink-0">
                          <div className={`h-9 w-9 rounded-xl border-2 border-dashed overflow-hidden flex items-center justify-center transition-all ${v.image_preview ? 'border-amber-400' : 'border-gray-200 hover:border-amber-300'}`}>
                            {v.image_preview
                              ? <img src={v.image_preview} className="w-full h-full object-cover" alt="" />
                              : <Upload className="h-4 w-4 text-gray-300" />}
                          </div>
                          <input type="file" accept="image/*" className="sr-only"
                            onChange={e => {
                              const file = e.target.files?.[0]; if (!file) return;
                              updateVariantField(i, 'image_file', file);
                              updateVariantField(i, 'image_preview', URL.createObjectURL(file));
                              e.target.value = '';
                            }} />
                        </label>
                      )}

                      <button onClick={() => removeVariant(i)}
                        data-testid={`delete-variant-${i}`}
                        className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Row 2: Price(s) + Stock */}
                    <div className="grid grid-cols-3 gap-2 pl-10">
                      {showDual ? (
                        <>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-400 uppercase font-semibold">Günlük Fiyat</label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{form.currency}</span>
                              <Input type="number" value={v.daily_price ?? ''}
                                onChange={e => updateVariantField(i, 'daily_price', e.target.value)}
                                className="rounded-lg pl-8 text-sm h-8" placeholder="0.00" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-400 uppercase font-semibold">Etkinlik Fiyatı</label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{form.currency}</span>
                              <Input type="number" value={v.event_price ?? ''}
                                onChange={e => updateVariantField(i, 'event_price', e.target.value)}
                                className="rounded-lg pl-8 text-sm h-8" placeholder="0.00" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="space-y-1 col-span-2">
                          <label className="text-[10px] text-gray-400 uppercase font-semibold">Fiyat</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{form.currency}</span>
                            <Input type="number" value={v.daily_price ?? ''}
                              onChange={e => updateVariantField(i, 'daily_price', e.target.value)}
                              className="rounded-lg pl-8 text-sm h-8" placeholder="0.00" />
                          </div>
                        </div>
                      )}
                      <div className="space-y-1">
                        <label className="text-[10px] text-gray-400 uppercase font-semibold">Stok</label>
                        <Input type="number" value={v.stock ?? ''}
                          onChange={e => updateVariantField(i, 'stock', e.target.value)}
                          className="rounded-lg text-sm h-8" min="0" placeholder="0" />
                      </div>
                    </div>

                    {/* Row 3: Code + Color label */}
                    <div className="flex items-center gap-2 pl-10">
                      <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                        {v.variant_code || `YK-XXXX-V${String(i + 1).padStart(2, '0')}`}
                      </span>
                      {colorItem && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Palette className="h-2.5 w-2.5" />{colorItem.name}
                        </span>
                      )}
                    </div>

                    {/* Variant image panel — only for saved variants */}
                    {isExpanded && vid && (
                      <div data-testid={`variant-image-panel-${vid}`} className="border-t border-gray-100 bg-white rounded-xl px-4 py-3 mt-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
                          <Image className="h-3 w-3" /> Varyant Görselleri ({imgs.length}/5)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {imgs.map(img => (
                            <div key={img.id} data-testid={`variant-image-thumb-${img.id}`} className="relative group h-20 w-20 shrink-0 rounded-xl overflow-hidden border-2 border-gray-200">
                              <SecureImage src={img.thumb_url || img.url} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button onClick={() => handleDeleteVariantImage(img.id, vid)}
                                  data-testid={`variant-image-delete-${img.id}`}
                                  className="text-white hover:text-red-300 p-1">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                          {imgs.length < 5 && (
                            <>
                              <input
                                ref={el => { if (el) variantFileRefs.current[vid] = el; }}
                                type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={e => { handleUploadVariantImage(vid, e.target.files); e.target.value = ''; }}
                              />
                              <button
                                data-testid={`variant-image-upload-btn-${vid}`}
                                onClick={() => variantFileRefs.current[vid]?.click()}
                                disabled={isUploading}
                                className="h-20 w-20 rounded-xl border-2 border-dashed border-amber-200 flex flex-col items-center justify-center text-amber-400 hover:bg-amber-50 transition-colors gap-1">
                                {isUploading
                                  ? <Loader2 className="h-5 w-5 animate-spin" />
                                  : <><Upload className="h-4 w-4" /><span className="text-[10px]">Ekle</span></>}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add variant buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            {variants.length < 20 && (
              <button onClick={addVariant}
                data-testid="edit-add-variant-btn"
                className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> Varyant Ekle
              </button>
            )}
            {selectedPatternIds.size > 0 && (
              <button
                type="button"
                onClick={() => {
                  const baseDaily = form.daily_price || '';
                  const baseEvent = form.event_price || '';
                  const baseStock = form.min_order_quantity || '';
                  const colorVariants = [...selectedPatternIds].map(cid => {
                    const item = availablePatternItems.find(p => p.id === cid);
                    return { _new: true, _dirty: true, name: item?.name || '', daily_price: baseDaily, event_price: baseEvent, stock: baseStock, color_id: cid, image_file: null, image_preview: null };
                  });
                  setVariants(prev => [...prev, ...colorVariants]);
                  setOpenColorPicker(null);
                }}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1.5 border border-purple-200 rounded-xl px-3 py-1.5 hover:bg-purple-50 transition-colors"
              >
                <Palette className="h-3.5 w-3.5" />
                Seçili renklerden varyant oluştur ({selectedPatternIds.size})
              </button>
            )}
            {availablePatternItems.length > 0 && selectedPatternIds.size === 0 && (
              <button
                type="button"
                onClick={() => {
                  const baseDaily = form.daily_price || '';
                  const baseEvent = form.event_price || '';
                  const baseStock = form.min_order_quantity || '';
                  const colorVariants = availablePatternItems.map(item => ({
                    _new: true, _dirty: true, name: item.name, daily_price: baseDaily, event_price: baseEvent, stock: baseStock, color_id: item.id, image_file: null, image_preview: null
                  }));
                  setVariants(prev => [...prev, ...colorVariants]);
                  setOpenColorPicker(null);
                }}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1.5 border border-purple-200 rounded-xl px-3 py-1.5 hover:bg-purple-50 transition-colors"
              >
                <Palette className="h-3.5 w-3.5" />
                Tüm renklerden varyant oluştur ({availablePatternItems.length})
              </button>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="p-6 flex gap-3">
          <Button
            data-testid="product-edit-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-12 text-base font-semibold gap-2">
            {saving
              ? <><Loader2 className="h-5 w-5 animate-spin" />Kaydediliyor...</>
              : <><Check className="h-5 w-5" />Değişiklikleri Kaydet</>}
          </Button>
          <Button variant="outline" onClick={onBack} className="rounded-xl px-6">İptal</Button>
        </div>
      </div>
    </div>
  );
}
